import React, { useState, useEffect, useCallback } from 'react';
import { Navbar } from './components/Navbar';
import { BottomNav } from './components/BottomNav';
import { ClosetView } from './components/ClosetView';
import { AddItemModal } from './components/AddItemModal';
import { ItemDetailModal } from './components/ItemDetailModal';
import { TryOnStudio } from './components/TryOnStudio';
import { LookbookView } from './components/LookbookView';
import { BodyProfileModal } from './components/BodyProfileModal';
import { SettingsModal } from './components/SettingsModal';
import { AccountModal } from './components/AccountModal';
import { AuthModal } from './components/AuthModal';
import {
  ClothingItem,
  BodyProfile,
  Outfit,
  AppSettings,
  User,
} from './types';
import { api, getToken, clearToken } from './lib/api';

export const App: React.FC = () => {
  // Current user & auth
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthChecking, setIsAuthChecking] = useState<boolean>(true);
  const [isInitialSetup, setIsInitialSetup] = useState<boolean>(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false);

  // App navigation
  const [activeTab, setActiveTab] = useState<'closet' | 'studio' | 'lookbook'>('closet');

  // Main data state
  const [clothes, setClothes] = useState<ClothingItem[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [sizes, setSizes] = useState<string[]>([]);
  const [bodyProfiles, setBodyProfiles] = useState<BodyProfile[]>([]);
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [settings, setSettings] = useState<AppSettings | null>(null);

  // Loading states
  const [isLoadingClothes, setIsLoadingClothes] = useState<boolean>(false);
  const [isLoadingOutfits, setIsLoadingOutfits] = useState<boolean>(false);

  // Modal states
  const [isAddItemOpen, setIsAddItemOpen] = useState<boolean>(false);
  const [selectedDetailItem, setSelectedDetailItem] = useState<ClothingItem | null>(null);
  const [isBodyProfilesOpen, setIsBodyProfilesOpen] = useState<boolean>(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const [isAccountOpen, setIsAccountOpen] = useState<boolean>(false);

  // Preselected garment for try-on studio
  const [preselectedGarment, setPreselectedGarment] = useState<ClothingItem | null>(null);

  // Toast alert
  const [toastMessage, setToastMessage] = useState<string>('');

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3500);
  };

  // Load user data
  const loadUserData = useCallback(async () => {
    setIsLoadingClothes(true);
    setIsLoadingOutfits(true);

    try {
      const [clothesRes, profilesRes, outfitsRes, settingsRes] = await Promise.all([
        api.clothes.list(),
        api.profiles.list(),
        api.outfits.list(),
        api.settings.get(),
      ]);

      setClothes(clothesRes.clothes);
      setBrands(clothesRes.brands);
      setSizes(clothesRes.sizes);
      setBodyProfiles(profilesRes.profiles);
      setOutfits(outfitsRes.outfits);
      setSettings(settingsRes);
    } catch (err: any) {
      console.error('Failed to load user data', err);
    } finally {
      setIsLoadingClothes(false);
      setIsLoadingOutfits(false);
    }
  }, []);

  // Initial Auth Check
  useEffect(() => {
    const initAuth = async () => {
      setIsAuthChecking(true);
      const token = getToken();

      try {
        if (token) {
          const meRes = await api.auth.me();
          setCurrentUser(meRes.user);
          await loadUserData();
        } else {
          const setupRes = await api.auth.checkSetup();
          setIsInitialSetup(!setupRes.is_setup);
          setIsAuthModalOpen(true);
        }
      } catch (err) {
        clearToken();
        const setupRes = await api.auth.checkSetup().catch(() => ({ is_setup: true }));
        setIsInitialSetup(!setupRes.is_setup);
        setIsAuthModalOpen(true);
      } finally {
        setIsAuthChecking(false);
      }
    };

    initAuth();

    // Listen to session expiry
    const handleAuthExpired = () => {
      setCurrentUser(null);
      setIsAuthModalOpen(true);
    };
    window.addEventListener('auth:expired', handleAuthExpired);
    return () => window.removeEventListener('auth:expired', handleAuthExpired);
  }, [loadUserData]);

  // Auth success
  const handleAuthSuccess = async (user: User) => {
    setCurrentUser(user);
    setIsAuthModalOpen(false);
    await loadUserData();
    showToast(`Welcome to FitCheck, ${user.username}!`);
  };

  // Logout
  const handleLogout = async () => {
    try {
      await api.auth.logout();
    } catch (err) {
      // Ignore
    }
    clearToken();
    setCurrentUser(null);
    setClothes([]);
    setOutfits([]);
    setBodyProfiles([]);
    setIsAccountOpen(false);
    setIsAuthModalOpen(true);
    showToast('Logged out successfully.');
  };

  // Switch / Add Account
  const handleSwitchAccount = () => {
    clearToken();
    setCurrentUser(null);
    setClothes([]);
    setOutfits([]);
    setBodyProfiles([]);
    setIsAccountOpen(false);
    setIsAuthModalOpen(true);
  };

  // Toggle favorite for an item
  const handleToggleFavorite = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.clothes.toggleFavorite(id);
      setClothes((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, is_favorite: item.is_favorite ? 0 : 1 } : item
        )
      );
    } catch (err) {
      console.error(err);
    }
  };

  // Log wear for an individual item
  const handleLogWear = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.clothes.logWear(id);
      setClothes((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, wear_count: (item.wear_count || 0) + 1 } : item
        )
      );
      const target = clothes.find((item) => item.id === id);
      showToast(`Logged wear for "${target?.name || 'clothing'}" (+1)`);
    } catch (err) {
      console.error(err);
    }
  };

  // Send item directly to Try-On Studio
  const handleSendToTryOn = (item: ClothingItem, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setPreselectedGarment(item);
    setActiveTab('studio');
    showToast(`Added ${item.name} to Try-On Studio!`);
  };

  // Clothing item created
  const handleItemAdded = (newItem: ClothingItem) => {
    setClothes((prev) => [newItem, ...prev]);
    if (newItem.brand && !brands.includes(newItem.brand)) {
      setBrands((prev) => [...prev, newItem.brand]);
    }
    if (newItem.size && !sizes.includes(newItem.size)) {
      setSizes((prev) => [...prev, newItem.size]);
    }
    showToast(`Added "${newItem.name}" to your closet!`);
  };

  // Clothing item updated
  const handleItemUpdated = (updatedItem: ClothingItem) => {
    setClothes((prev) =>
      prev.map((item) => (item.id === updatedItem.id ? updatedItem : item))
    );
    setSelectedDetailItem(updatedItem);
    showToast('Clothing item updated.');
  };

  // Clothing item deleted
  const handleItemDeleted = (id: string) => {
    setClothes((prev) => prev.filter((item) => item.id !== id));
    setSelectedDetailItem(null);
    showToast('Clothing item removed from closet.');
  };

  // Body Profile added
  const handleProfileAdded = (newProfile: BodyProfile) => {
    setBodyProfiles((prev) => {
      if (newProfile.is_default) {
        return [newProfile, ...prev.map((p) => ({ ...p, is_default: 0 }))];
      }
      return [...prev, newProfile];
    });
    showToast('Body photo saved for try-on!');
  };

  // Body profile default set
  const handleProfileSetDefault = async (id: string) => {
    try {
      await api.profiles.setDefault(id);
      setBodyProfiles((prev) =>
        prev.map((p) => ({
          ...p,
          is_default: p.id === id ? 1 : 0,
        }))
      );
      showToast('Default try-on photo updated.');
    } catch (err) {
      console.error(err);
    }
  };

  // Body profile deleted
  const handleProfileDeleted = async (id: string) => {
    try {
      await api.profiles.delete(id);
      setBodyProfiles((prev) => prev.filter((p) => p.id !== id));
      showToast('Body photo deleted.');
    } catch (err) {
      console.error(err);
    }
  };

  // Outfit saved from studio
  const handleOutfitSaved = (newOutfit: Outfit) => {
    setOutfits((prev) => [newOutfit, ...prev]);
    showToast('Outfit saved to your Lookbook!');
  };

  if (isAuthChecking) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-brand-600 animate-pulse flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
        <p className="text-sm font-semibold text-slate-400">Loading FitCheck Virtual Closet...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      {/* Toast Alert */}
      {toastMessage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-slate-900/95 text-white px-5 py-2.5 rounded-2xl shadow-2xl border border-brand-500/40 text-xs sm:text-sm font-semibold backdrop-blur-md animate-in fade-in slide-in-from-top-4 duration-200">
          {toastMessage}
        </div>
      )}

      {/* Top Navbar */}
      <Navbar
        user={currentUser}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onOpenAddItem={() => setIsAddItemOpen(true)}
        onOpenBodyProfiles={() => setIsBodyProfilesOpen(true)}
        onOpenAccount={() => setIsAccountOpen(true)}
        onOpenSettings={() => setIsSettingsOpen(true)}
        onLogout={handleLogout}
        hasGeminiKey={!!settings?.has_gemini_key}
        clothesCount={clothes.length}
      />

      {/* Main Content Area */}
      <main className="flex-1">
        {activeTab === 'closet' && (
          <ClosetView
            clothes={clothes}
            brands={brands}
            sizes={sizes}
            isLoading={isLoadingClothes}
            onOpenAddItem={() => setIsAddItemOpen(true)}
            onSelectItem={(item) => setSelectedDetailItem(item)}
            onToggleFavorite={handleToggleFavorite}
            onLogWear={handleLogWear}
            onSendToTryOn={handleSendToTryOn}
          />
        )}

        {activeTab === 'studio' && (
          <TryOnStudio
            clothes={clothes}
            bodyProfiles={bodyProfiles}
            preselectedItem={preselectedGarment}
            onClearPreselected={() => setPreselectedGarment(null)}
            onOpenBodyProfiles={() => setIsBodyProfilesOpen(true)}
            onOutfitSaved={handleOutfitSaved}
            hasGeminiKey={!!settings?.has_gemini_key}
          />
        )}

        {activeTab === 'lookbook' && (
          <LookbookView
            outfits={outfits}
            isLoading={isLoadingOutfits}
            onOutfitUpdated={(up) =>
              setOutfits((prev) => prev.map((o) => (o.id === up.id ? up : o)))
            }
            onOutfitDeleted={(id) =>
              setOutfits((prev) => prev.filter((o) => o.id !== id))
            }
            onSwitchToStudio={() => setActiveTab('studio')}
          />
        )}
      </main>

      {/* Mobile Bottom Navigation Bar */}
      <BottomNav
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onOpenAddItem={() => setIsAddItemOpen(true)}
        onOpenBodyProfiles={() => setIsBodyProfilesOpen(true)}
      />

      {/* Modals */}
      <AddItemModal
        isOpen={isAddItemOpen}
        onClose={() => setIsAddItemOpen(false)}
        onItemAdded={handleItemAdded}
        hasGeminiKey={!!settings?.has_gemini_key}
      />

      <ItemDetailModal
        item={selectedDetailItem}
        isOpen={!!selectedDetailItem}
        onClose={() => setSelectedDetailItem(null)}
        onItemUpdated={handleItemUpdated}
        onItemDeleted={handleItemDeleted}
        onSendToTryOn={handleSendToTryOn}
      />

      <BodyProfileModal
        isOpen={isBodyProfilesOpen}
        onClose={() => setIsBodyProfilesOpen(false)}
        profiles={bodyProfiles}
        onProfileAdded={handleProfileAdded}
        onProfileSetDefault={handleProfileSetDefault}
        onProfileDeleted={handleProfileDeleted}
      />

      <AccountModal
        isOpen={isAccountOpen}
        onClose={() => setIsAccountOpen(false)}
        currentUser={currentUser}
        onLogout={handleLogout}
        onSwitchAccount={handleSwitchAccount}
      />

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={settings}
        onSettingsSaved={loadUserData}
      />

      <AuthModal
        isOpen={isAuthModalOpen}
        isInitialSetup={isInitialSetup}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
};
